import { AssetTypeEnum, AssetTypeLabels } from '@api/enum';
import { IAssetPurchaseOrder } from '@api/models';
import { Button, Card, HelpText, Label } from '@hyperflake/react-ui-library';
import { ErrorMessage, Form, Formik } from 'formik';
import { useAssetPurchaseOrderUpdateForm } from 'modules/asset-purchase-order/hooks/useAssetPurchaseOrderUpdateForm';
import FileUploadProjectInstallation from 'modules/project-installation/components/FileUploadProjectInstallation/FileUploadProjectInstallation';
import { FormikInput, FormikSelect } from 'modules/shared/features/formik';
import FormikTextarea from 'modules/shared/features/formik/components/FormikTextarea/FormikTextarea';
// import { FormikSelect,  } from 'modules/shared/features/formik';
// import { useAssetPurchaseOrderUpdateForm } from 'modules/asset/hooks/useAssetPurchaseOrderUpdateForm';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import FileUploadAssetPurchaseOrder from '../FileUploadAssetPurchaseOrder/FileUploadAssetPurchaseOrder';
import { useNavigate } from 'react-router-dom';
import useDialog from '@hooks/use-dialog';
import DeleteAssetPurchaseOrderDrawer from '../DeleteAssetPurchaseOrderDrawer/DeleteAssetPurchaseOrderDrawer';
import { LockIcon, UnlockIcon } from '@icon/index';

interface UpdateAssetPurchaseOrderFormProps {
    clientInstallationId: string;
    projectInstallationId: string;
    initialData: IAssetPurchaseOrder;
    setData: (data: any) => void;
}

const UpdateAssetPurchaseOrderForm = ({
    clientInstallationId,
    projectInstallationId,
    initialData,
    setData,
}: UpdateAssetPurchaseOrderFormProps) => {
    const formHook = useAssetPurchaseOrderUpdateForm(clientInstallationId, projectInstallationId, initialData);
    const navigate = useNavigate();
    const deletePurchaseOrderDailog = useDialog<IAssetPurchaseOrder>();
    const [enableEdit, setEnableEdit] = useState<boolean>(false);

    const assetTypeOptions = useMemo(() => {
        return [
            { label: 'Select Asset', value: '' },
            ...Object.values(AssetTypeEnum).map((val) => ({
                label: AssetTypeLabels[val], // Display human-readable label
                value: val,
            })),
        ];
    }, []);

    const handleSubmit = async (values: any) => {
        try {
            await formHook.submit(values);
            toast.success('Asset Purchase Order Updated Successfully');
            navigate(
                `/client-installations/${clientInstallationId}/project-installations/${projectInstallationId}/purchase-order`
            );
        } catch (err: any) {
            toast.error(err?.data?.message || 'Something went wrong.');
        }
    };

    return (
        <>
            <Formik
                initialValues={formHook.initialValues}
                validationSchema={formHook.schema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isSubmitting }) => (
                    <Form>
                        <Card className="mt-4">
                            <Card.Header className="flex justify-between items-center">
                                <div>Basic Information</div>
                                <div>
                                    {enableEdit ? (
                                        <div className="flex gap-4">
                                            <Button type="button" variant="shaded" onClick={() => setEnableEdit(false)}>
                                                Cancel
                                            </Button>
                                            <Button disabled={formHook.isSubmitting || !enableEdit}>
                                                {formHook.isSubmitting ? 'Updating...' : 'Save'}
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button type="button" variant="shaded" onClick={() => setEnableEdit(true)}>
                                            {!enableEdit ? (
                                                <LockIcon width={18} height={22} className="text-grayscale-600" />
                                            ) : (
                                                <UnlockIcon width={18} height={22} className="text-grayscale-600" />
                                            )}
                                            Edit
                                        </Button>
                                    )}
                                </div>
                            </Card.Header>
                            <Card.Body>
                                <fieldset disabled={!enableEdit}>
                                    <div>
                                        <Label>Remarks</Label>
                                        <FormikTextarea name="remarks" rows={4} disabled={!enableEdit} />
                                        <HelpText variant="error">
                                            <ErrorMessage name="remarks" />
                                        </HelpText>
                                    </div>

                                    <div className="mt-4">
                                        <Label>Type of Asset</Label>
                                        <FormikSelect name="typeOfAssets" options={assetTypeOptions} />
                                        <HelpText variant="error">
                                            <ErrorMessage name="typeOfAssets" />
                                        </HelpText>
                                    </div>
                                    {/*Quantity */}
                                    <div className="mt-4">
                                        <Label>Quantity</Label>
                                        <FormikInput name="quantity" type="number" />
                                        <HelpText variant="error">
                                            <ErrorMessage name="quantity" />
                                        </HelpText>
                                    </div>
                                </fieldset>
                            </Card.Body>
                        </Card>

                        <Button type="submit" className="mt-6 w-full sm:w-auto" disabled={isSubmitting || !enableEdit}>
                            {isSubmitting ? 'Updating...' : 'Update Order'}
                        </Button>
                    </Form>
                )}
            </Formik>

            <FileUploadAssetPurchaseOrder
                clientInstallationId={clientInstallationId}
                projectInstallationId={projectInstallationId}
                initialData={initialData}
                setData={setData}
                enableEdit={enableEdit}
            />
            {enableEdit && (
                <Card className="mt-4">
                    <Card.Header>Delete Purchase Order</Card.Header>
                    <Card.Body>
                        <Button onClick={() => deletePurchaseOrderDailog.show(initialData)}>Delete</Button>
                    </Card.Body>
                </Card>
            )}

            <DeleteAssetPurchaseOrderDrawer
                clientInstallationId={clientInstallationId}
                projectInstallationId={projectInstallationId}
                onClose={deletePurchaseOrderDailog.hide}
                open={deletePurchaseOrderDailog.isOpen}
                assetPurchaseOrder={deletePurchaseOrderDailog.data}
            />
        </>
    );
};

export default UpdateAssetPurchaseOrderForm;
